<template>
    <div>
        <div class="flex justify-between flex-col-reverse sm:flex-row sm:items-center">
            <div class="flex sm:items-center flex-col sm:flex-row">
                <table class="mx-2">
                    <tr>
                        <th scope="row" class="text-right text-sm whitespace-nowrap">{{ $t('commons.version') }} :</th>
                        <td class="px-3">
                            <app-select
                                :label="$t('commons.version')"
                                :value="cie.id"
                                class=""
                                :show-label="false"
                                :disabled="!cie.id"
                                @change="onVersionSelected($event)"
                            >
                                <option :value="version.id" v-for="(version, index) in cie.versions" :key="version.id">
                                    {{ cie.versions.length - index }} ({{ version.createdAt | humanizeDate }})
                                </option>
                            </app-select>
                        </td>
                    </tr>
                </table>

                <div class="m-2 sm:my-0">
                    <app-button @click="onNewVersion" :disabled="!cie.id">
                        {{ $t('commons.newVersion') }}
                    </app-button>
                </div>
            </div>
        </div>
        <ValidationObserver v-slot="{ dirty }" ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-4" :label="$t('commons.infos')">
                    <app-input-text
                        :label="$t('commons.object')"
                        v-model="cie.object"
                        class="col-span-2 md:col-span-1"
                        :required="true"
                        @blur="save()"
                        ref="objectInput"
                    />
                    <app-input-text
                        :label="$t('commons.code')"
                        v-model="cie.code"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-bundle-picker
                        class="col-span-2"
                        v-model="cie.bundle"
                        :options="bundles"
                        @input="onBundleChange"
                    ></app-bundle-picker>
                    <app-input-text
                        :label="$t('amendments.quoteReference')"
                        v-model="cie.quoteReference"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-multi-picker
                        class="col-span-2"
                        v-model="cie.locations"
                        :options="locations"
                        :show-label="true"
                        label-key="fullName"
                        @input="save()"
                        :label="$t('commons.locations')"
                        :title="$t('commons.chooseLocations')"
                    ></app-multi-picker>
                    <app-number-input
                        :label="$t('commons.amountHT')"
                        v-model="cie.amountHT"
                        :allowsNegative="true"
                        @blur="save()"
                        :currency="currencyFormat.currency"
                    />
                    <app-number-input
                        :label="$t('commons.amountTTC')"
                        v-model="cie.amountTTC"
                        :allowsNegative="true"
                        @blur="save()"
                        :currency="currencyFormat.currency"
                    />
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('commons.dates')">
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="cie.emissionDueDate"
                        :label="$t('commons.emissionDueDate')"
                        @input="save()"
                    ></app-date-input>
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="cie.emissionDate"
                        :label="$t('commons.emissionDate')"
                        @input="save()"
                    />
                </app-fieldset>
                <app-save-on-leave :dirty="dirty" :saveFn="() => save(true)"></app-save-on-leave>

                <div class="col-span-2 flex flex-col mt-5 gap-2 text-sm">
                    <app-separator :label="$t('cie.cieDistribution')" class="mt-5"></app-separator>
                    <div class="flex gap-2 items-center">
                        <app-multi-picker
                            v-model="selectedBundles"
                            @check="onBundleCheck"
                            @uncheck="onBundleUncheck"
                            @close="onBundleClose"
                            :options="bundles"
                            variant="button"
                            :show-label="false"
                            :show-input="false"
                            :label="$t('cie.newDistribution')"
                            :title="$t('cie.newDistribution')"
                            :filterStringFunction="(bundle) => bundle.reference + ' ' + bundle.name"
                        >
                            <template v-slot:option="{ option }">
                                <app-bundle :bundle="option" />
                            </template>
                        </app-multi-picker>

                        <hsc-menu-style-white>
                            <hsc-menu-button-menu>
                                <app-button size="mini" :title="$t('cie.autoDistribution')" class="px-2">
                                    <icon-arrow-decision-outline width="16px" heigth="16px" />
                                </app-button>
                                <template slot="contextmenu">
                                    <hsc-menu-item :label="$t('cie.equalDistribution')" @click="distributeEqual()" />
                                    <hsc-menu-item
                                        :label="$t('cie.equalMarketProRataDistribution')"
                                        @click="distributeProRata()"
                                    />
                                    <hsc-menu-item
                                        v-if="!allHave0 && distributionSum !== 0 && distributionSum < cie.amountTTC"
                                        :label="$t('cie.missingEqualDistribution')"
                                        @click="distributeMissingEqual()"
                                    />
                                    <hsc-menu-item
                                        v-if="someHave0 && !allHave0 && distributionSum < cie.amountTTC"
                                        :label="$t('cie.missingEqual0Distribution')"
                                        @click="distributeMissingIf0()"
                                    />
                                </template>
                            </hsc-menu-button-menu>
                        </hsc-menu-style-white>
                    </div>
                    <table class="w-full border" v-if="distribution.length > 0">
                        <tr class="border-b">
                            <th class="p-2 px-4 text-left" colspan="2">{{ $t('cie.cieBundle') }}</th>
                            <th class="p-2 px-4 text-right">{{ $t('commons.amountTTC') }}</th>
                        </tr>
                        <tr class="border-b" v-for="line in distribution">
                            <td class="border p-2 px-4">
                                <span v-if="line.bundle.reference">#{{ line.bundle.reference }}</span>
                            </td>
                            <td class="border p-2 px-4 text-left" style="width: 70%">
                                <span>{{ line.bundle.name }}</span>
                            </td>
                            <td class="border p-2 px-4 text-right">
                                <app-number-link
                                    :label="$t('commons.amountTTC')"
                                    :show-label="false"
                                    v-model="line.amount"
                                    @input="save()"
                                    :positiveOnly="false"
                                    format="decimal"
                                    class="underline"
                                >
                                    <span class="w-full text-right">{{ $n(line.amount, currencyFormat) }}</span>
                                </app-number-link>
                            </td>
                        </tr>
                        <tr class="border-b">
                            <td colspan="2" class="border p-2 px-4 text-right">
                                <span>{{ $t('cie.cieTotal') }}</span>
                            </td>
                            <td colspan="2" class="border p-2 px-4 text-right">
                                <span class="w-full text-right">{{ $n(distributionSum, currencyFormat) }}</span>
                            </td>
                        </tr>
                        <tr class="border-b font-bold text-red-600" v-if="distributionSum !== cie.amountTTC">
                            <td colspan="3" class="border p-2 px-4 text-right">
                                <span class="w-full text-right" v-if="distributionSum > cie.amountTTC">
                                    <span>{{ $t('cie.cieTooMuchTotal') }}</span>
                                    ({{ $n(distributionSum - cie.amountTTC, currencyFormat) }})
                                </span>
                                <span class="w-full text-right" v-else>
                                    <span>{{ $t('cie.cieMissingTotal') }} :</span>
                                    ({{ $n(cie.amountTTC - distributionSum, currencyFormat) }})
                                </span>
                            </td>
                        </tr>
                    </table>
                </div>
                <div class="col-span-2 mt-5">
                    <app-separator :label="$t('commons.status')" class="mt-5"></app-separator>
                    <table class="w-full border">
                        <tr class="border-b">
                            <td class="p-2 px-4 text-left">
                                {{ $t('amendments.quoteStatusTitle') }}
                            </td>
                            <td class="p-2 px-4 text-left">
                                <div class="flex flex-col gap-2">
                                    <app-select
                                        :label="$t('amendments.quoteStatusTitle')"
                                        :show-label="false"
                                        v-model="cie.quoteStatus"
                                        @input="save()"
                                    >
                                        <option value="toEmit">{{ $t('amendments.quoteStatus.toEmit') }}</option>
                                        <option value="toAnalyse">{{ $t('amendments.quoteStatus.toAnalyse') }}</option>
                                        <option value="toNegotiate">
                                            {{ $t('amendments.quoteStatus.toNegotiate') }}
                                        </option>
                                        <option value="toValidateMOA">
                                            {{ $t('amendments.quoteStatus.toValidateMOA') }}
                                        </option>
                                        <option value="validated">{{ $t('amendments.quoteStatus.validated') }}</option>
                                        <option value="rejected">{{ $t('amendments.quoteStatus.rejected') }}</option>
                                        <option value="obsolete">{{ $t('amendments.quoteStatus.obsolete') }}</option>
                                    </app-select>
                                    <app-label
                                        :label="$t('amendments.quoteComment')"
                                        v-if="cie.quoteStatus === 'rejected' || cie.quoteStatus === 'obsolete'"
                                    >
                                        <app-textarea v-model="cie.quoteComment" @blur="save()"></app-textarea>
                                    </app-label>
                                </div>
                            </td>
                        </tr>
                    </table>
                </div>
            </form>
        </ValidationObserver>
    </div>
</template>

<script>
import { getMapById, reduceSumOn, sanitize } from '@/services/sanitize.service';
import { combineLatest, map } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getLocationsTree } from '@/features/locations/locations.service';
import locationService from '@/services/location.service';

import { isMobile } from '@/state/state';
import AppLabel from '@/components/appLabel/AppLabel.vue';
import { getCompanies } from '@/features/companies/companies.service';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput.vue';

import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import AppTextEditor from '@/components/appTextEditor/AppTextEditor.vue';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink.vue';
import { queryProject } from '@/features/projects/projects.service';
import AppInputText from '@/components/appInputText/AppInputText.vue';
import AppBundlePicker from '@/components/appBundlePicker.vue';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker.vue';
import AppFieldset from '@/components/appFieldset/AppFieldset.vue';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';
import AppSaveOnLeave from '@/components/AppSaveOnLeave.vue';
import AppSeparator from '@/components/appSeparator/AppSeparator.vue';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppBundle from '@/components/app-bundle/appBundle.vue';
import IconArrowDecisionOutline from '@/icons/IconArrowDecisionOutline.vue';
import { getAmendments } from '@/features/amendments/amendments.service';
import { createCie, getCie, updateCie } from '@/features/cie/CIE.service';

export default {
    components: {
        AppNumberLink,
        IconArrowDecisionOutline,
        AppBundle,
        AppButton,
        AppTextarea,
        AppLabel,
        AppSelect,
        AppSeparator,
        AppSaveOnLeave,
        AppDateInput,
        AppFieldset,
        AppNumberInput,
        AppMultiPicker,
        AppBundlePicker,
        AppInputText,
        AppTextEditor,
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
        });
        this.init();
        localStorage.setItem('cie.lastVisitedCieId.' + this.$route.params.projectId, this.$route.params.cieId);
    },
    watch: {
        $route: function (from, to) {
            if (from.name !== to.name || from.params.cieId !== to.params.cieId) {
                this.focusDone = false;
            }
            this.init();
        },
    },
    computed: {
        distributionSum() {
            return this.distribution.reduce((acc, line) => acc + line.amount, 0);
        },
        someHave0() {
            return !!this.distribution.find((line) => line.amount === 0);
        },
        allHave0() {
            return this.distribution.every((line) => line.amount === 0);
        },
    },
    methods: {
        distributeEqual() {
            const slitAmount = this.cie.amountTTC / this.distribution.length;
            this.distribution.map((line) => {
                line.amount = slitAmount;
            });
            this.save();
        },
        distributeMissingIf0() {
            const lines0 = this.distribution.filter((line) => line.amount === 0);
            const slitAmount = (this.cie.amountTTC - this.distributionSum) / lines0.length;
            lines0.map((line) => {
                line.amount = slitAmount;
            });
            this.save();
        },
        distributeMissingEqual() {
            const slitAmount = (this.cie.amountTTC - this.distributionSum) / this.distribution.length;
            this.distribution.map((line) => {
                line.amount += slitAmount;
            });
            this.save();
        },
        distributeProRata() {
            const totalMarketAmount = this.distribution.reduce((acc, line) => acc + line.bundle.marketAmount, 0);
            this.distribution = this.distribution.map((line) => {
                line.amount =
                    Math.round((line.bundle.marketAmount / totalMarketAmount) * this.cie.amountTTC * 100) / 100;
                return line;
            });
            this.save();
        },
        onBundleCheck(bundles) {
            bundles.map((bundle) => {
                this.distribution.push({ bundleId: bundle.id, bundle, amount: 0 });
            });
        },
        onBundleClose() {
            this.save();
        },
        onBundleUncheck(bundles) {
            bundles.map(async (bundle) => {
                this.distribution = this.distribution.filter((line) => line.bundleId !== bundle.id);
            });
        },
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getLocationsTree(this.$route.params.projectId).pipe(
                        map((folders) => locationService.buildLocationOptions(folders)),
                    ),
                    getAmendments(this.$route.params.projectId),
                    getCie(this.$route.params.projectId, this.$route.params.cieId),
                ]).subscribe(([bundles, companies, locations, amendments, cie]) => {
                    this.locations = locations;
                    const marketSignedAmendments = amendments.filter(
                        (amendment) =>
                            amendment.bundleId === cie.bundleId &&
                            amendment.type === 'market' &&
                            amendment.amendmentStatus === 'signed',
                    );
                    const locationMap = getMapById(locations);
                    const companyMap = getMapById(companies);
                    const mappedBundles = bundles.map((bundle) => ({
                        ...bundle,
                        company: companyMap[bundle.companyId],
                        marketAmount: reduceSumOn(marketSignedAmendments, 'amountTTC'),
                    }));
                    this.bundleMap = getMapById(mappedBundles);
                    this.cie = {
                        ...cie,
                        bundle: mappedBundles.find((bundle) => bundle.id === cie.bundleId),
                        locations: cie.locationIds.map((id) => locationMap[id]),
                    };
                    this.bundles = mappedBundles;
                    this.selectedVersion = this.$route.params.cieId;
                    if (!this.focusDone && !isMobile && this.cie.object === '') {
                        this.$nextTick(() => {
                            this.$refs.objectInput.focus();
                        });
                        this.focusDone = true;
                    }
                    if (this.cie.distribution) {
                        this.selectedBundles = this.cie.distribution.map((line) => this.bundleMap[line.bundleId]);
                        this.distribution = this.cie.distribution.map((line) => ({
                            ...line,
                            bundle: this.bundleMap[line.bundleId],
                        }));
                    } else {
                        this.selectedBundles = [];
                        this.distribution = [];
                    }
                }),
            ];
        },
        onBundleChange() {
            this.save();
        },
        async onVersionSelected(cieId) {
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    cieId,
                },
            });
        },
        async onNewVersion() {
            const cieEntity = {
                groupId: this.cie.groupId,
                object: this.cie.object,
                code: this.cie.code,
                emissionDate: null,
                emissionDueDate: null,
                bundleId: this.cie.bundle ? this.cie.bundle.id : null,
                locationIds: this.cie.locationIds ? this.cie.locationIds : [],
                distribution: this.cie.distribution ? this.cie.distribution : [],
                amountHT: this.cie.amountHT,
                amountTTC: this.cie.amountTTC,
            };
            const result = await createCie(this.$route.params.projectId, cieEntity);
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    cieId: result.id,
                },
            });
        },
        sanitizeCie(cie, toOmit = []) {
            return {
                ...sanitize(cie, ['bundle', 'locations', 'versions', ...toOmit]),
                bundleId: cie.bundle ? cie.bundle.id : null,
                locationIds: cie.locations.map((location) => location.id),
            };
        },
        async save() {
            if (this.savingFlag) {
                return;
            } else {
                this.savingFlag = true;
            }
            try {
                const cieEntity = this.sanitizeCie(this.cie);
                await updateCie(this.$route.params.projectId, {
                    id: this.cie.id,
                    ...cieEntity,
                    distribution: this.distribution.map((distribution) => ({
                        bundleId: distribution.bundleId,
                        amount: distribution.amount,
                    })),
                });
                if (this.$refs.observer) {
                    this.$refs.observer.reset();
                }
            } finally {
                this.savingFlag = false;
            }
        },
        async createNew() {
            const result = await createCie(this.$route.params.projectId, {
                name: '',
            });
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    cieId: result.id,
                },
            });
        },
    },
    data() {
        return {
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            focusDone: false,
            distribution: [],
            subscriptions: [],
            bundleMap: {},
            savingFlag: false,
            bundles: [],
            bundlesToCopy: [],
            cie: {
                name: '',
                locations: [],
                distribution: [],
                bundle: null,
                attachments: [],
                versions: [],
            },
            selectedVersion: null,
            project: {},
            locations: [],
            selectedBundles: [],
        };
    },
};
</script>
